import {JsonObject} from "@/common/json/json-object";
import {JsonProperty} from "@/common/json/json-property";
import {Type} from "@/common/json/helpers";
import {$KTM, $KTS, KeyMetadatas, KeyTextStrings} from "@/common/keytext";
import {DefaultObjectLoader, TypedObject} from "@/shared/types";
import {FormGen} from "@/shared/formgen";
import {ProviderTourHelper} from "./ProviderTourHelper";
import {BaseApp} from "@/shared/BaseApp";

@JsonObject()
export abstract class BaseSettings extends TypedObject {

  constructor() {
    super(null, null);
  }

  protected basePath(): string | null {
    return "settings/members";
  }

  createDefaultLoader(): DefaultObjectLoader<any> {
    return new DefaultObjectLoader<any>(this.basePath(), this.id, this.getType());
  }
}

export enum OnboardingStatus {
  NONE = "none",
  SKIPPED = "skipped",
  COMPLETED = "completed",
}

@JsonObject()
export class OnboardingSettings extends BaseSettings {

  readonly id: string = "onboarding";

  @JsonProperty()
  status: OnboardingStatus = OnboardingStatus.NONE;

  @JsonProperty()
  tourShown?: boolean;

  static async load() {
    const obj = new OnboardingSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return OnboardingSettings;
  }
}

@JsonObject()
export class GeneralSettings extends BaseSettings {

  readonly id: string = "general";

  @FormGen({name: "Account name", type: "string", sectionTitle: "Account details"})
  @JsonProperty()
  accountName: string;

  // @FormGen({name: "Account ID", type: "string", readonly: true})
  // @JsonProperty()
  // accountId: string = getProvisioningId();

  @FormGen({
    sectionTitle: "SSL",
    name: "Certificate validation",
    type: "boolean",
    description: "Determines if certificate validation is performed on all FyneAI originated requests."
  })
  @JsonProperty()
  certValidation: boolean = true;

  static async load() {
    const obj = new GeneralSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return GeneralSettings;
  }
}

@JsonObject()
export class NotificationsSettings extends BaseSettings {

  readonly id = "notifications";

  @FormGen({name: "Pricing Updates", description: "Updates to product prices and rates.", type: "boolean"})
  @JsonProperty()
  pricingUpdates: boolean;

  static async load() {
    const obj = new NotificationsSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return NotificationsSettings;
  }
}

export enum Receive2FA {
  TEXT = "text",
  CALL = "call",
}

const RECEIVE_2FA_KTS = new KeyTextStrings([
  $KTS(Receive2FA.TEXT, "Text"),
  $KTS(Receive2FA.CALL, "Call"),
])

@JsonObject()
export class AccountSecuritySettings extends BaseSettings {

  readonly id = "account_security";

  @FormGen({
    name: "Receive 2FA code",
    description: "How do you want to receive your 2FA code.",
    type: "enum",
    enumValues: RECEIVE_2FA_KTS.values,
    enumDefaultKey: Receive2FA.TEXT
  })
  @JsonProperty()
  receive2FA: Receive2FA = Receive2FA.TEXT;

  static async load() {
    const obj = new AccountSecuritySettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return AccountSecuritySettings;
  }
}

export enum FontSize {
  SMALL = "small",
  DEFAULT = "default",
  LARGE = "large",
}

const FONT_SIZE_KTS = new KeyTextStrings([
  $KTS(FontSize.SMALL, "Small"),
  $KTS(FontSize.DEFAULT, "Default"),
  $KTS(FontSize.LARGE, "Large"),
]);

export enum Timezone {
  AMERICA_LOS_ANGELES = "america_los_angeles",
}

export type TimezoneMetadata = {
  text: string,
  gmt: number,
}

const TIMEZONE_KTM = new KeyMetadatas<TimezoneMetadata>([
  $KTM(Timezone.AMERICA_LOS_ANGELES, {text: "America/Los Angeles", gmt: -8}),
]);

export enum NavLayout {
  TABS = "tabs",
  SIDEBAR = "sidebar",
}

const NAV_LAYOUT_KTS = new KeyTextStrings([
  $KTS(NavLayout.TABS, "Tabs"),
  $KTS(NavLayout.SIDEBAR, "Sidebar"),
]);

export enum NavSidebarColorScheme {
  LIGHT = "light",
  DARK = "dark",
}

const NAV_SIDEBAR_COLOR_SCHEME_KTS = new KeyTextStrings([
  $KTS(NavSidebarColorScheme.LIGHT, "Light"),
  $KTS(NavSidebarColorScheme.DARK, "Dark"),
]);

@JsonObject()
export class PreferencesSettings extends BaseSettings {

  readonly id = "preferences";

  @FormGen({
    name: "Timezone",
    description: "Select the timezone you'd like to use to display dates and times.",
    type: "enum",
    enumValues: TIMEZONE_KTM.kts().values,
    enumDefaultKey: Timezone.AMERICA_LOS_ANGELES,
  })
  @JsonProperty()
  timezone: string = Timezone.AMERICA_LOS_ANGELES;

  @FormGen({
    name: "Font size",
    type: "enum",
    description: "Adjust your font size.",
    enumValues: FONT_SIZE_KTS.values,
    enumDefaultKey: FontSize.DEFAULT,
  })
  @JsonProperty()
  fontSize: string = FontSize.DEFAULT;

  @FormGen({
    name: "Reset Tour",
    type: "button",
    buttonDescription: "Resets any tour settings to its default value",
    buttonText: "Reset",
    buttonOnClick: target => {
      ProviderTourHelper.setShown(false);
      BaseApp.CONTEXT.showToast("Tour settings have been reset.");
    }
  })
  @JsonProperty()
  resetTour: void = null;

  @FormGen({
    name: "Experimental features",
    type: "boolean",
    description: "Enable or disable features that are not launched yet. An experimental feature may be unstable and can be removed in the future without warning.",
  })
  @JsonProperty()
  experimental: boolean = false;

  static async load() {
    const obj = new PreferencesSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return PreferencesSettings;
  }
}

@JsonObject()
export class AppearanceLayoutSettings {

  @FormGen({
    name: "Navigation Layout",
    type: "enum",
    enumValues: NAV_LAYOUT_KTS.values,
    enumDefaultKey: NavLayout.TABS,
  })
  @JsonProperty()
  navLayout: string = NavLayout.TABS;

  @FormGen({
    name: "Navigation Sidebar Color Scheme",
    type: "enum",
    enumValues: NAV_SIDEBAR_COLOR_SCHEME_KTS.values,
    enumDefaultKey: NavSidebarColorScheme.DARK,
  })
  @JsonProperty()
  navSidebarColorScheme: string = NavSidebarColorScheme.DARK;
}

@JsonObject()
export class AppearanceSettings extends BaseSettings {

  readonly id = "appearance";

  @FormGen({
    name: "Layout",
    type: "object",
  })
  @JsonProperty()
  readonly layout: AppearanceLayoutSettings = new AppearanceLayoutSettings();

  static async load() {
    const obj = new AppearanceSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return AppearanceSettings;
  }
}

export abstract class BaseProfileSettings {

  static getInstance(): BaseProfileSettings {
    return globalThis.ProfileSettings;
  }

  constructor(
    readonly preferences: PreferencesSettings,
    readonly appearance: AppearanceSettings,
  ) {
  }
}

export class ProviderSettings extends BaseProfileSettings {

  static getInstance(): ProviderSettings {
    return globalThis.ProfileSettings as ProviderSettings;
  }

  static async loadInstance() {
    globalThis.ProfileSettings = new ProviderSettings(
      await PreferencesSettings.load(),
      await AppearanceSettings.load(),
      await OnboardingSettings.load(),
      // await GeneralSettings.load(),
      // await NotificationsSettings.load(),
      // await AccountSecuritySettings.load(),
    );
  }

  constructor(
    preferences: PreferencesSettings,
    appearance: AppearanceSettings,
    readonly onboarding: OnboardingSettings,
    // readonly general: GeneralSettings,
    // readonly notifications: NotificationsSettings,
    // readonly accountSecurity: AccountSecuritySettings,
  ) {
    super(preferences, appearance);
  }
}